import { useCallback } from 'react';

/**
 * Common props interface for modal components
 */
interface CommonModalProps {
  onClose: () => void;
  disableBackdropClick?: boolean;
  disableEscapeKeyDown?: boolean;
}

/**
 * Custom hook to provide consistent modal behavior across the application
 * Ensures proper click-outside behavior for modals, menus, dialogs, and drawers
 */
export const useModalBehavior = () => {
  /**
   * Get common props for all modal components
   * @param options Configuration options
   * @returns Common props object
   */
  const getCommonProps = useCallback(
    ({ onClose, disableBackdropClick = false, disableEscapeKeyDown = false }: CommonModalProps) => {
      return {
        // Enable click-outside behavior
        onClose,
        // Ensure backdrop is clickable unless disabled
        BackdropProps: {
          onClick: disableBackdropClick ? undefined : onClose,
          sx: { cursor: disableBackdropClick ? 'default' : 'pointer' }
        },
        // Pass props down to the underlying MUI Modal component
        ModalProps: {
          // Disable scroll lock to prevent page jumping
          disableScrollLock: true,
          // Enable escape key to close modal unless disabled
          disableEscapeKeyDown,
          // Enable smooth transitions
          closeAfterTransition: true,
        },
      };
    },
    []
  );

  /**
   * Get props for Material UI Menu component
   * @param onClose Function to call when the menu should close
   * @param options Additional options
   * @returns Props object to spread on Menu component
   */
  const getMenuProps = useCallback(
    (onClose: () => void, options: Omit<CommonModalProps, 'onClose'> = {}) => {
      return {
        ...getCommonProps({ onClose, ...options }),
        // Don't keep the menu mounted when closed to improve performance
        keepMounted: false,
        // Prevent focus issues
        disableRestoreFocus: false,
        // Prevent portal issues
        disablePortal: false,
      };
    },
    [getCommonProps]
  );

  /**
   * Get props for Material UI Dialog component
   * @param onClose Function to call when the dialog should close
   * @param options Additional options
   * @returns Props object to spread on Dialog component
   */
  const getDialogProps = useCallback(
    (onClose: () => void, options: Omit<CommonModalProps, 'onClose'> = {}) => {
      return {
        ...getCommonProps({ onClose, ...options }),
        // Prevent focus issues
        disableRestoreFocus: false,
        // Prevent portal issues
        disablePortal: false,
      };
    },
    [getCommonProps]
  );

  /**
   * Get props for Material UI Popover component
   * @param onClose Function to call when the popover should close
   * @param options Additional options
   * @returns Props object to spread on Popover component
   */
  const getPopoverProps = useCallback(
    (onClose: () => void, options: Omit<CommonModalProps, 'onClose'> = {}) => {
      return {
        ...getCommonProps({ onClose, ...options }),
        // Don't keep the popover mounted when closed to improve performance
        keepMounted: false,
        // Prevent focus issues
        disableRestoreFocus: false,
        // Prevent portal issues
        disablePortal: false,
      };
    },
    [getCommonProps]
  );

  /**
   * Get props for Material UI Drawer component
   * @param onClose Function to call when the drawer should close
   * @param options Additional options
   * @returns Props object to spread on Drawer component
   */
  const getDrawerProps = useCallback(
    (onClose: () => void, options: Omit<CommonModalProps, 'onClose'> = {}) => {
      const common = getCommonProps({ onClose, ...options });
      return {
        ...common,
        // Modal props for drawer
        ModalProps: {
          ...common.ModalProps,
          keepMounted: false, // Don't keep the drawer mounted when closed
          disablePortal: false, // Use portal for proper stacking
        },
      };
    },
    [getCommonProps]
  );

  return {
    getMenuProps,
    getDialogProps,
    getPopoverProps,
    getDrawerProps,
  };
};

export default useModalBehavior;
